import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useWallet } from '../components/WalletContext';
import { 
  FaShoppingBag, 
  FaSpinner, 
  FaImage,
  FaExclamationTriangle,
  FaShoppingCart,
  FaCoins
} from 'react-icons/fa';
import '../styles/WalletPurchasesPage.css';

import skyGalleryImage from '../assets/skygallery.png';
import klevernftImage from '../assets/klever.png';
import minthreeImage from '../assets/minthree.png';
import xportImage from '../assets/xport.png';
import RareCanvasImage from '../assets/logoicon2.png';
import kDEXImage from '../assets/kdex.png';
import world from '../assets/world.png';
import artGallery from '../assets/artgallery.png';

const marketplaceMapping = {
  '7c353c02770da029': 'Sky Gallery',
  'd4f2bab340c55fde': 'KleverNFT',
  '293693ee43db509': 'Minthree',
  'd2a04fe890a6edda': 'NFT Art Gallery',
  'aa68ad853a09988c': 'Xport',
  '417b70c0eb7a33cb': 'Rare Canvas',
  '116056b257d9f6d5': 'kDEX',
  '81376f526cf47730': 'World dex'
};

const marketplaceImages = {
  'Sky Gallery': skyGalleryImage,
  'KleverNFT': klevernftImage,
  'Minthree': minthreeImage,
  'Xport': xportImage,
  'Rare Canvas': RareCanvasImage,
  'kDEX': kDEXImage,
  'World dex': world,
  'NFT Art Gallery': artGallery,
};

// Utility: Create axios instance with timeout
const createAxiosWithTimeout = (timeout = 5000) => {
  return axios.create({
    timeout,
    headers: { 'Content-Type': 'application/json' }
  });
};

// Utility: Fetch with timeout and retry
const fetchWithTimeout = async (url, timeout = 5000, retries = 2) => {
  const axiosInstance = createAxiosWithTimeout(timeout);

  for (let i = 0; i < retries; i++) {
    try {
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      // On final retry, check if it's a timeout or network error
      if (i === retries - 1) {
        if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') {
          // Network/timeout errors - fail silently for non-critical calls
          console.warn('Network timeout for:', url);
          return null;
        }
        throw error;
      }
      await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
    }
  }
};

const WalletPurchasesPage = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [totalPurchases, setTotalPurchases] = useState(0);
  const [totalSpent, setTotalSpent] = useState(0);
  const { walletAddress } = useWallet();

  // Fetch metadata with comprehensive error handling
  const fetchMetadata = async (collectionId, nftId) => {
    try {
      // Fetch asset list with timeout
      const assetData = await fetchWithTimeout(
        'https://api.mainnet.klever.org/v1.0/assets/list?limit=100',
        5000
      );

      // Handle null response from timeout
      if (!assetData || !assetData.data || !assetData.data.assets) {
        return null;
      }

      const assets = assetData.data.assets;
      const matchingAsset = assets.find(a => a.assetId === collectionId);
      
      if (!matchingAsset) {
        console.warn('Asset not found for collection ID:', collectionId);
        return null;
      }

      const metadataObject = matchingAsset.uris?.find(uri => uri.key.toLowerCase() === 'metadata');
      let metadataUri = metadataObject ? metadataObject.value : null;

      if (!metadataUri) {
        console.warn('No metadata URI found for:', nftId);
        return null;
      }

      // Handle special cases
      if (metadataUri.includes("klever-mint.mypinata.cloud")) {
        metadataUri = metadataUri.replace("https://klever-mint.mypinata.cloud", "https://ipfs.io");
      }
      metadataUri = `${metadataUri}/${nftId}`;

      // Try primary fetch with timeout
      try {
        const metadata = await fetchWithTimeout(metadataUri, 5000, 1);
        
        // Fix image URLs
        if (metadata.image) {
          if (metadata.image.includes("www.lovemonsternft.com")) {
            const imageName = metadata.image.split('/').pop();
            metadata.image = `https://ipfs.io/ipfs/QmWVmUDPBeQzv6fG93JxQxFVee8b6smFD3RQosQXJHiZTJ/${imageName}`;
          } else if (metadata.image.includes("klever-mint.mypinata.cloud")) {
            metadata.image = metadata.image.replace("https://klever-mint.mypinata.cloud", "https://ipfs.io");
          }
        }
        
        return metadata;
      } catch (primaryError) {
        // Try fallback
        console.warn('Primary fetch failed, trying fallback for', nftId);
        try {
          const fallbackUri = `https://${metadataUri}.ipfs.w3s.link/${nftId}`;
          const fallbackData = await fetchWithTimeout(fallbackUri, 5000, 1);
          return fallbackData;
        } catch (fallbackError) {
          console.error('Fallback also failed for', nftId);
          return null;
        }
      }
    } catch (error) {
      console.error(`Error fetching metadata for ${collectionId}/${nftId}:`, error);
      return null;
    }
  };

  // Fetch transactions with proper error handling
  const fetchTransactions = useCallback(async (page) => {
    setLoading(true);
    setErrorMessage('');

    try {
      const data = await fetchWithTimeout(
        `https://api.mainnet.klever.org/v1.0/transaction/list?limit=100&page=${page}&type=17&buyType=1&status=success&fromAddress=${walletAddress}`,
        15000, // Increased timeout for main fetch
        3 // Extra retry for main fetch
      );

      // Check for null response
      if (!data || !data.data || !data.data.transactions) {
        setErrorMessage('Unable to connect to blockchain API. Please try again later.');
        setTransactions([]);
        setLoading(false);
        return;
      }

      // Use Promise.allSettled to handle failures gracefully
      const transactionPromises = data.data.transactions.map(async (transaction) => {
        try {
          const receiptWithAssetId = transaction.receipts.find(
            (receipt) =>
              receipt.typeString === "KAppTransfer" && receipt.assetId !== "KLV"
          );

          if (!receiptWithAssetId) {
            return null;
          }

          const { assetId, marketplaceId } = receiptWithAssetId;
          const price = transaction.contract[0].parameter.amount / 1000000;
          const marketplaceName = marketplaceMapping[marketplaceId] || 'Unknown Marketplace';

          // Fetch NFT metadata
          const [collectionId, nftNonce] = assetId.split('/');
          
          // Fetch metadata with error handling
          const metadataResult = await Promise.race([
            fetchMetadata(collectionId, nftNonce),
            new Promise((resolve) => setTimeout(() => resolve(null), 5000))
          ]);

          const imageUrl = metadataResult?.image || null;

          return {
            ...transaction,
            assetId,
            collectionId,
            nftNonce,
            price,
            marketplaceName,
            imageUrl,
            hasError: false
          };
        } catch (error) {
          console.error('Error processing transaction:', error);
          // Return partial data even on error
          const receiptWithAssetId = transaction.receipts.find(
            (receipt) => receipt.typeString === "KAppTransfer" && receipt.assetId !== "KLV"
          );
          
          if (receiptWithAssetId) {
            const { assetId } = receiptWithAssetId;
            const [collectionId, nftNonce] = assetId.split('/');
            return {
              ...transaction,
              assetId,
              collectionId,
              nftNonce,
              price: transaction.contract[0].parameter.amount / 1000000,
              marketplaceName: 'Unknown',
              imageUrl: null,
              hasError: true
            };
          }
          return null;
        }
      });

      const results = await Promise.allSettled(transactionPromises);
      const validTxs = results
        .filter(result => result.status === 'fulfilled' && result.value !== null)
        .map(result => result.value);

      setTransactions(validTxs);
      setTotalPages(data.pagination.totalPages);
      
      // Calculate stats
      setTotalPurchases(validTxs.length);
      const spent = validTxs.reduce((sum, tx) => sum + tx.price, 0);
      setTotalSpent(spent);
    } catch (error) {
      console.error('Error fetching transactions:', error);

      // Provide more specific error messages
      if (error.code === 'ECONNABORTED') {
        setErrorMessage('Request timed out. The blockchain API may be slow. Please try again.');
      } else if (error.code === 'ERR_NETWORK') {
        setErrorMessage('Network error. Please check your connection and try again.');
      } else if (error.response?.status === 429) {
        setErrorMessage('Too many requests. Please wait a moment and try again.');
      } else {
        setErrorMessage('Failed to load purchases. Please try again later.');
      }

      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [walletAddress]);

  useEffect(() => {
    if (walletAddress) {
      fetchTransactions(currentPage);
    }
  }, [currentPage, walletAddress, fetchTransactions]);

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (!walletAddress) {
    return (
      <div className="wallet-purchases-page">
        <div className="purchases-loading">
          <FaExclamationTriangle 
            className="purchases-loading-spinner" 
            style={{ fontSize: '3rem', color: 'var(--warning)' }} 
          />
          <p className="purchases-loading-text">Please connect your wallet to view purchases.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="wallet-purchases-page">
      {/* Hero Section */}
      <div className="purchases-hero">
        <h1 className="purchases-hero-title">
          <FaShoppingBag className="purchases-hero-icon" />
          Your Purchases
        </h1>
        <p className="purchases-hero-subtitle">
          View your NFT purchase history on KleverChain
        </p>
      </div>

      {loading ? (
        <div className="purchases-loading">
          <FaSpinner className="purchases-loading-spinner" />
          <p className="purchases-loading-text">Loading your purchases...</p>
        </div>
      ) : (
        <>
          {/* Error Message */}
          {errorMessage && (
            <div className="purchases-error">
              <FaExclamationTriangle />
              <span>{errorMessage}</span>
            </div>
          )}

          {/* Stats Banner */}
          {transactions.length > 0 && (
            <div className="purchases-stats">
              <div className="purchases-stat-card">
                <div className="purchases-stat-icon-wrapper">
                  <FaShoppingCart className="purchases-stat-icon" />
                </div>
                <div className="purchases-stat-content">
                  <span className="purchases-stat-label">Total Purchases</span>
                  <span className="purchases-stat-value">{totalPurchases}</span>
                </div>
              </div>
              <div className="purchases-stat-card">
                <div className="purchases-stat-icon-wrapper">
                  <FaCoins className="purchases-stat-icon" />
                </div>
                <div className="purchases-stat-content">
                  <span className="purchases-stat-label">Total Spent</span>
                  <span className="purchases-stat-value">
                    {totalSpent.toLocaleString(undefined, { 
                      minimumFractionDigits: 2, 
                      maximumFractionDigits: 2 
                    })} KLV
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Purchases Container */}
          <div className="purchases-container">
            {transactions.length === 0 ? (
              <div className="purchases-empty-state">
                <FaShoppingBag 
                  className="purchases-empty-icon" 
                  style={{ opacity: 0.5 }}
                />
                <h2>No Purchases Found</h2>
                <p>You haven't purchased any NFTs yet. Start exploring the marketplace!</p>
              </div>
            ) : (
              transactions.map((transaction, index) => {
                const marketplaceImage = marketplaceImages[transaction.marketplaceName];
                return (
                  <Link
                    to={`/collections/${transaction.collectionId}/${transaction.nftNonce}`}
                    key={`${transaction.hash}-${index}`}
                    className="wallet-purchases-activity-item-link"
                  >
                    <div 
                      className="wallet-purchases-activity-item"
                      style={{ animationDelay: `${index * 0.05}s` }}
                    >
                      {/* NFT Image */}
                      {transaction.imageUrl ? (
                        <img
                          src={transaction.imageUrl}
                          alt={`NFT ${transaction.assetId}`}
                          className="wallet-purchases-listings-nft-image"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div 
                        className="wallet-purchases-nft-image-placeholder"
                        style={{ display: transaction.imageUrl ? 'none' : 'flex' }}
                      >
                        <FaImage />
                      </div>

                      {/* Details */}
                      <div className="wallet-purchases-activity-details">
                        <p>
                          <span className="wallet-purchases-date">
                            {formatDate(transaction.timestamp)}
                          </span>{' '}
                          — You bought{' '}
                          <span className="wallet-purchases-asset-id">
                            {transaction.assetId}
                          </span>{' '}
                          for{' '}
                          <span className="wallet-purchases-listed-price">
                            {transaction.price.toLocaleString()} KLV
                          </span>
                        </p>
                        
                        <div className="wallet-purchases-marketplace-info">
                          <span className="wallet-purchases-marketplace">
                            {transaction.marketplaceName}
                          </span>
                          {marketplaceImage && (
                            <img
                              src={marketplaceImage}
                              alt={transaction.marketplaceName}
                              className="wallet-purchases-marketplace-image"
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })
            )}
          </div>

          {/* Pagination */}
          {transactions.length > 0 && (
            <div className="wallet-purchases-pagination">
              <button onClick={handlePrevPage} disabled={currentPage <= 1}>
                <span>Previous</span>
              </button>
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <button onClick={handleNextPage} disabled={currentPage >= totalPages}>
                <span>Next</span>
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default WalletPurchasesPage;